/**
 * Générateur — Attendu 186 — « Il utilise les cas d'égalité des triangles pour
 * rédiger et mettre en œuvre un protocole de construction de figures. »
 * Niveau : 4ème | Thème #11 Géométrie plane | Espace et géométrie.
 *
 * ── REVUE 2026-09-24 (vague 5 du chantier « 8 attendus de géométrie 4e ») ──
 * L'intitulé n'existe nulle part tel quel : c'est Eduscol fin de 4e (2019)
 * l. 389-397 (« À partir des connaissances suivantes : les cas d'égalité des
 * triangles ; […] il met en œuvre et écrit un protocole de construction de
 * figures »), restreint par le prof à sa première connaissance. Les cas
 * d'égalité sont ABSENTS du BO 2025 (cycles 3 et 4) : ils viennent du
 * programme 2020 (repères 2019, 4e : « Les cas d'égalité des triangles sont
 * présentés et utilisés […] Le lien est fait avec la construction d'un
 * triangle de mesures données ») ; c'est le choix du prof, tenu par ses
 * exercices (image 5 de son document : construire A'B'C' égal à ABC à partir
 * de [A'B'] sur quadrillage ; les triangles jumeaux ; les deux diamètres).
 *
 * LES QUATRE ANCIENNES CARTES SORTENT (décision prof 24/09 : « les cartes
 * existantes si y en a qui conviennent pas tu supprimes ») : aucune ne touchait
 * un cas d'égalité. « Ordonner un protocole » construisait l'image d'un
 * triangle par translation (187 le fait), « Justifier une mesure d'angle »
 * était la somme des angles (5e) avec un distracteur faux présenté comme une
 * propriété, « Nature du quadrilatère construit » et « Caractériser l'image
 * d'un point » sont la translation (182, 187). La carte « Cas d'égalité des
 * triangles » de 190 (sans figure) est sortie aussi : elle revit ici avec une
 * figure codée.
 *
 * ── Cartes ────────────────────────────────────────────────────────────────
 *   « Le troisième sommet » (sommet_placer_points_2d) : sur quadrillage, le
 *     triangle ABC et le segment [A'B'] (déplacé, tourné ou retourné) ; placer
 *     C' pour que A'B'C' soit égal à ABC, du côté imposé de (A'B') (les deux
 *     solutions du manuel deviennent deux tirages). Le « mettre en œuvre ».
 *   « Le protocole dans l'ordre » (protocole_ordonner) : les étapes de la
 *     construction d'un triangle égal à ABC, pour les trois cas (trois côtés ;
 *     deux côtés et l'angle compris ; un côté et deux angles), en chaîne
 *     linéaire. Le « rédiger ».
 *   « Pourquoi sont-ils égaux ? » (cas_qcm_unique) : une figure codée
 *     (cerf-volant, papillon, triangles jumeaux, deux triangles sur un côté,
 *     triangle isocèle et sa bissectrice) ; « D'après ces données, quel cas
 *     d'égalité permet d'affirmer que les triangles … sont égaux ? ». Le QCM
 *     de l'attendu.
 *   Coupées (épreuve de Musk) : la construction au compas à l'écran (le
 *   point d'intersection ne se corrige pas), « Lesquels sont égaux au
 *   triangle ① ? » (reconnaître des triangles superposables : pas un cas
 *   d'égalité).
 *   Pas de rappel brouillon (tout se fait à l'écran).
 *
 * ── Erreurs (familles nommées ; aucun chiffre DEPP, inférence) ────────────
 *   cote_commun_oublie (PRINCIPALE) / angle_oublie / cote_oublie : la
 *     justification incomplète, deux éléments sur trois (le QCM) ;
 *   element_non_donne : un autre cas, cité avec des égalités que rien ne
 *     donne (le QCM) ; angles_seuls (« trois angles égaux, donc triangles
 *     égaux ») : SORTIE du QCM le 25/09, c'est le contre-exemple de la fiche ;
 *   autre_solution (C' de l'autre côté de (A'B')) ; mauvais_sommet ;
 *   etape_avant_son_objet.
 *
 * SACRIFIÉ (fiche à venir) : le triangle de côtés (5 ; 4 ; 6) (AB ; AC ; BC),
 * jamais tiré dans le protocole.
 *
 * ── Révision 2026-09-25 (retour prof sur « Pourquoi sont-ils égaux ? ») ──
 *   - La question demande le CAS qui prouve, plus un « pourquoi » : « D'après
 *     ces données, quel cas d'égalité permet d'affirmer que les triangles …
 *     sont égaux ? ». Une phrase vraie n'est pas une raison : sur les
 *     jumeaux, « trois angles de même mesure » est vrai (la somme des angles)
 *     et reste faux comme justification, c'est l'erreur angles_seuls qu'on
 *     mesure. Une seule bonne réponse, toujours.
 *   - Triangles jumeaux : le second angle droit ne vaut que si le pied de la
 *     hauteur est sur la base, ce que le dessin montrait sans le dire. L'énoncé
 *     le dit désormais : « V est un point du segment [ST] » (lettres du
 *     tirage, _meta.params.alignement). Choix prof : la phrase plutôt qu'un
 *     second carré codé ; l'élève déduit l'angle droit du second triangle.
 *   - Les quatre réponses sont INSTANCIÉES avec les lettres de la figure,
 *     comme la justification du cours (« côtés correspondants », l'accent
 *     sur les angles) : « Un côté commun et les deux angles qui le touchent
 *     de même mesure : GA commun, EGA = RGA, EAG = RAG. » Le cas d'abord, les
 *     égalités après deux-points, un seul « et » (prof : « trop de et, ce
 *     n'est pas français »). Voir `reponses`.
 *   - « Trois angles » SORTIE des réponses (prof : « l'un implique l'autre »,
 *     la somme des angles la rend vraie sur les jumeaux et le côté commun ;
 *     sur les autres figures rien ne la donne, l'élève l'écartait pour une
 *     mauvaise raison). À sa place, la justification INCOMPLÈTE : la bonne
 *     moins un élément, « Deux angles correspondants de même mesure : CGM =
 *     CGL, GCM = GCL. » sans le côté commun. Tout y est donné, et ce n'est
 *     aucun des trois cas : une seule réponse vraie et complète, sans débat.
 *     Le contre-exemple des trois angles (un triangle et son agrandissement)
 *     va dans la fiche coup de pouce.
 */

import {
    randInt, randomChoice, pickLettresSains, tex,
    makeQcm, makeOrdonner, basePayload,
} from '../../../utils';
import { imgElement } from '../../5eme/statistiques/svgStats';
import { svgTriangle, tourner } from '../../3eme/geometrie-plane/figTriangle';

const ATTENDU_ID = 186;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_SOMMET = 'Le troisième sommet';
const L_PROTOCOLE = 'Le protocole dans l\'ordre';
const L_CAS = 'Pourquoi sont-ils égaux ?';

const POOL = 'ABCDEFGHJKLMNPRSTUV'.split('');
/** Le triangle de la fiche coup de pouce (AB ; AC ; BC) : jamais tiré. */
export const SACRIFIE = [5, 4, 6];

// ═══ Carte « Le troisième sommet » (quadrillage) ═══════════════════════════

const XMAX = 16; const YMAX = 10;
const plus = (p, v) => [p[0] + v[0], p[1] + v[1]];
const moins = (p, q) => [p[0] - q[0], p[1] - q[1]];
/** Les huit déplacements du quadrillage (rotations d'un quart de tour, retournements). */
const ISOMETRIES = [
    ([x, y]) => [x, y], ([x, y]) => [-y, x], ([x, y]) => [-x, -y], ([x, y]) => [y, -x],
    ([x, y]) => [-x, y], ([x, y]) => [x, -y], ([x, y]) => [y, x], ([x, y]) => [-y, -x],
];
const angleDeg = (p, q, r) => {
    const u = moins(q, p); const w = moins(r, p);
    return (Math.acos((u[0] * w[0] + u[1] * w[1]) / (Math.hypot(...u) * Math.hypot(...w))) * 180) / Math.PI;
};
const bienForme = ([a, b, c]) => Math.min(Math.hypot(...moins(b, a)), Math.hypot(...moins(c, b)), Math.hypot(...moins(a, c))) >= 2
    && Math.min(angleDeg(a, b, c), angleDeg(b, c, a), angleDeg(c, a, b)) >= 25;
const dans = (p, x0, x1) => p[0] >= x0 && p[0] <= x1 && p[1] >= 0 && p[1] <= YMAX;

/** Le côté de (A'B') où est C', dit comme sur un quadrillage : (A'B') horizontale ou verticale. */
const coteDe = (Ap, Bp, Cp) => (Ap[1] === Bp[1] ? (Cp[1] > Ap[1] ? 'au-dessus de' : 'en dessous de') : (Cp[0] > Ap[0] ? 'à droite de' : 'à gauche de'));

const genSommet = () => {
    for (let essai = 0; essai < 500; essai++) {
        // ABC à gauche ([AB] horizontal ou vertical), A'B'C' à droite, déplacé par l'un des huit déplacements du quadrillage
        const L = randInt(3, 6);
        const A = [randInt(0, 7), randInt(0, YMAX)];
        const B = plus(A, randomChoice([[L, 0], [0, L]]));
        const C = [randInt(0, 7), randInt(0, YMAX)];
        if (!dans(B, 0, 7) || !dans(C, 0, 7) || !bienForme([A, B, C])) continue;
        if (Math.hypot(...moins(C, A)) === Math.hypot(...moins(C, B))) continue;   // non isocèle en C : les deux positions de C' ne se confondent pas avec une symétrie évidente
        const f = randomChoice(ISOMETRIES);
        const Ap = [randInt(9, XMAX), randInt(0, YMAX)];
        const Bp = plus(Ap, f(moins(B, A))); const Cp = plus(Ap, f(moins(C, A)));
        if (![Ap, Bp, Cp].every((p) => dans(p, 9, XMAX))) continue;
        // l'autre solution (le symétrique de C' par rapport à (A'B')) est de l'autre côté : le côté imposé rend la clé unique
        const [lA, lB, lC] = pickLettresSains(POOL, 3);
        const noms = { A: lA, B: lB, C: lC };
        const np = { A: `${lA}'`, B: `${lB}'`, C: `${lC}'` };
        const cote = coteDe(Ap, Bp, Cp);
        const dejaPlaces = [
            { x: A[0], y: A[1], label: noms.A }, { x: B[0], y: B[1], label: noms.B }, { x: C[0], y: C[1], label: noms.C },
            { x: Ap[0], y: Ap[1], label: np.A }, { x: Bp[0], y: Bp[1], label: np.B },
        ];
        const texte = `<p>Le triangle ${tex(`${np.A}${np.B}${np.C}`)} est égal au triangle ${tex(`${noms.A}${noms.B}${noms.C}`)} : `
            + `${tex(`${np.A}${np.B} = ${noms.A}${noms.B}`)}, ${tex(`${np.A}${np.C} = ${noms.A}${noms.C}`)} et ${tex(`${np.B}${np.C} = ${noms.B}${noms.C}`)}.</p>`
            + `<p><b>Place le point ${tex(np.C)}, ${cote} la droite ${tex(`(${np.A}${np.B})`)}.</b></p>`;
        const payload = basePayload('association', texte, {
            subType: 'placer_points_2d',
            repere: { xMin: 0, yMin: 0, xMax: XMAX, yMax: YMAX, xStep: 1, yStep: 1, quadrillage: true },
            pointsToPlace: [np.C],
            dejaPlaces,
            decor: { polygones: [[noms.A, noms.B, noms.C], [np.A, np.B]] },
            bonne_reponse: JSON.stringify({ [np.C]: `${Cp[0]};${Cp[1]}` }),
        }, opts(L_SOMMET));
        payload._meta.params = { A, B, C, Ap, Bp, Cp, cote, distracteurs: [{ code: 'autre_solution' }, { code: 'mauvais_sommet' }] };
        return payload;
    }
    throw new Error('gen_186 : troisième sommet introuvable');
};

// ═══ Carte « Le protocole dans l'ordre » (ordonner) ════════════════════════

const frCm = (x) => `${String(x).replace('.', ',')} cm`;

/** Un triangle ABC (côtés entiers 3..8, angles de 30° à 110°) et le cas de construction. */
const tirerTriangle = () => {
    for (let essai = 0; essai < 300; essai++) {
        const ab = randInt(4, 8); const ac = randInt(3, 8); const bc = randInt(3, 8);
        if (ab + ac <= bc || ab + bc <= ac || ac + bc <= ab) continue;
        if ([ab, ac, bc].join() === SACRIFIE.join()) continue;
        const a = (Math.acos((ab * ab + ac * ac - bc * bc) / (2 * ab * ac)) * 180) / Math.PI;
        const b = (Math.acos((ab * ab + bc * bc - ac * ac) / (2 * ab * bc)) * 180) / Math.PI;
        const c = 180 - a - b;
        if (Math.min(a, b, c) < 30 || Math.max(a, b, c) > 110) continue;
        return { ab, ac, bc, a: Math.round(a), b: Math.round(b) };
    }
    return { ab: 6, ac: 4, bc: 5, a: 56, b: 41 };
};

const genProtocole = () => {
    const t = tirerTriangle();
    const [A, B, C] = pickLettresSains(POOL, 3);
    const Ap = `${A}'`; const Bp = `${B}'`; const Cp = `${C}'`;
    const cas = randomChoice(['ccc', 'cac', 'aca']);
    const seg = `Je trace le segment ${tex(`[${Ap}${Bp}]`)} de longueur ${frCm(t.ab)}.`;
    let etapes; let donnees;
    if (cas === 'ccc') {
        donnees = `${tex(`${A}${B} = ${t.ab}`)} cm, ${tex(`${A}${C} = ${t.ac}`)} cm et ${tex(`${B}${C} = ${t.bc}`)} cm`;
        etapes = [
            seg,
            `Je trace un arc de cercle de centre ${tex(Ap)} et de rayon ${frCm(t.ac)}, puis un arc de cercle de centre ${tex(Bp)} et de rayon ${frCm(t.bc)}.`,
            `Je nomme ${tex(Cp)} le point où les deux arcs se coupent.`,
            `Je trace les segments ${tex(`[${Ap}${Cp}]`)} et ${tex(`[${Bp}${Cp}]`)}.`,
        ];
    } else if (cas === 'cac') {
        donnees = `${tex(`${A}${B} = ${t.ab}`)} cm, ${tex(`${A}${C} = ${t.ac}`)} cm et ${tex(`\\widehat{${B}${A}${C}} = ${t.a}^{\\circ}`)}`;
        etapes = [
            seg,
            `Je trace une demi-droite d'origine ${tex(Ap)} qui fait un angle de ${t.a}° avec ${tex(`[${Ap}${Bp}]`)}.`,
            `Je place le point ${tex(Cp)} sur cette demi-droite, à ${frCm(t.ac)} de ${tex(Ap)}.`,
            `Je trace le segment ${tex(`[${Bp}${Cp}]`)}.`,
        ];
    } else {
        donnees = `${tex(`${A}${B} = ${t.ab}`)} cm, ${tex(`\\widehat{${B}${A}${C}} = ${t.a}^{\\circ}`)} et ${tex(`\\widehat{${A}${B}${C}} = ${t.b}^{\\circ}`)}`;
        etapes = [
            seg,
            `Je trace une demi-droite d'origine ${tex(Ap)} qui fait un angle de ${t.a}° avec ${tex(`[${Ap}${Bp}]`)}, et une demi-droite d'origine ${tex(Bp)} qui fait un angle de ${t.b}° avec ${tex(`[${Bp}${Ap}]`)}, du même côté.`,
            `Je nomme ${tex(Cp)} le point où les deux demi-droites se coupent.`,
            `Le triangle ${tex(`${Ap}${Bp}${Cp}`)} est construit : il est égal au triangle ${tex(`${A}${B}${C}`)}.`,
        ];
    }
    const texte = `<p>On connaît, dans le triangle ${tex(`${A}${B}${C}`)} : ${donnees}. On veut construire un triangle ${tex(`${Ap}${Bp}${Cp}`)} égal au triangle ${tex(`${A}${B}${C}`)}.</p>`
        + '<p><b>Remets les étapes du protocole de construction dans l\'ordre.</b></p>';
    const payload = makeOrdonner(etapes, texte, opts(L_PROTOCOLE));
    payload._meta.params = { cas, ...t };
    return payload;
};

// ═══ Carte « Pourquoi sont-ils égaux ? » (QCM, figure codée) ═══════════════

/** Les trois cas, le début de chaque réponse (la quatrième réponse, incomplète, n'en est aucun). */
export const CAS = {
    ccc: 'Trois côtés correspondants de même longueur',
    cac: 'Deux côtés correspondants de même longueur et l\'angle compris entre eux de même mesure',
    aca: 'Un côté',
};

/** Le cas que dit une réponse, reconnu à sa formule entière (la version incomplète n'en est aucun) ; null sinon. */
export const casDe = (reponse) => (/^Trois côtés correspondants/.test(reponse) ? 'ccc'
    : /^Deux côtés correspondants .* et l'angle compris/.test(reponse) ? 'cac'
        : /^Un côté .* et les deux angles/.test(reponse) ? 'aca' : null);

// ── Les réponses instanciées (prof 25/09) ─────────────────────────────────
// Écrites comme la justification du cours, avec les lettres du tirage : le cas
// d'abord, puis les égalités après deux-points, séparées par des virgules, le
// côté commun en dernier (« GA commun » plutôt que GA = GA) ; un seul « et »
// par phrase, celui du cas. Les angles portent leur accent (\widehat). La
// correspondance des sommets est l'ordre des lettres de t1 et t2 (E ↔ R,
// G ↔ G, A ↔ A). Les deux autres cas, instanciés de la même façon, citent
// au moins un élément que la figure ne code pas : c'est ce que l'élève doit
// vérifier (« d'où je tiens ça ? »). La quatrième réponse est la justification
// INCOMPLÈTE : la bonne moins un élément (le côté commun s'il y en a un, sinon
// l'angle compris), tout ce qu'elle dit est donné, et ce n'est aucun des
// trois cas. « Trois angles » est SORTIE (prof 25/09) : vraie par la somme des
// angles sur les figures à deux angles donnés, invérifiable sur les autres,
// elle ne pouvait jamais bien piéger ; le contre-exemple va dans la fiche.

const paireCommune = (t1, t2, i, j) => [...`${t1[i]}${t1[j]}`].sort().join('') === [...`${t2[i]}${t2[j]}`].sort().join('');
/** Le côté [i, j] des deux triangles, comme au cours : « EG = RG », ou « GA commun » quand c'est le même segment. */
const coteTexte = (t1, t2, a, b) => {
    const [i, j] = [Math.min(a, b), Math.max(a, b)];
    const s1 = `${t1[i]}${t1[j]}`; const s2 = `${t2[i]}${t2[j]}`;
    return paireCommune(t1, t2, i, j) ? `${tex(s1)} commun` : tex(`${s1} = ${s2}`);
};
/** L'angle au sommet i des deux triangles, avec son accent : « EGA = RGA ». */
const angleTexte = (t1, t2, i) => {
    const [p, q] = [0, 1, 2].filter((k) => k !== i);
    return tex(`\\widehat{${t1[p]}${t1[i]}${t1[q]}} = \\widehat{${t2[p]}${t2[i]}${t2[q]}}`);
};
/** Le côté commun en dernier. */
const communEnDernier = (t1, t2, paires) => [...paires].sort((x, y) => Number(paireCommune(t1, t2, ...x)) - Number(paireCommune(t1, t2, ...y)));

/**
 * Les quatre réponses pour une configuration. Le cas clé cite exactement ce
 * qui est codé : `sommetCompris` (l'indice du sommet de l'angle compris, cas
 * cac) ou `coteAngles` (les deux indices du côté qui porte les deux angles,
 * cas aca) ; les autres cas prennent, à défaut, le côté commun ou le sommet 0.
 */
const reponses = (c) => {
    const t1 = [...c.t1]; const t2 = [...c.t2];
    const paires = [[0, 1], [1, 2], [0, 2]];
    const v = c.sommetCompris ?? (c.coteAngles ? c.coteAngles[0] : 0);
    const cote = c.coteAngles ?? (paires.find(([i, j]) => paireCommune(t1, t2, i, j)) || [0, 1]);
    const pairesCcc = communEnDernier(t1, t2, paires);
    const cotesCcc = pairesCcc.map(([i, j]) => coteTexte(t1, t2, i, j));
    const pairesCac = communEnDernier(t1, t2, [0, 1, 2].filter((k) => k !== v).map((k) => [v, k]));
    const cotesCac = pairesCac.map(([i, j]) => coteTexte(t1, t2, i, j)); const angleCac = angleTexte(t1, t2, v);
    const acaCommun = paireCommune(t1, t2, ...cote);
    const coteAca = coteTexte(t1, t2, ...cote); const anglesAca = [angleTexte(t1, t2, cote[0]), angleTexte(t1, t2, cote[1])];
    // La quatrième réponse : la justification INCOMPLÈTE, la bonne moins un
    // élément (le côté commun quand il y en a un, sinon l'angle compris) ;
    // tout ce qu'elle dit est donné, et ce n'est aucun des trois cas.
    const incomplet = {
        ccc: { texte: `Deux côtés correspondants de même longueur : ${cotesCcc.slice(0, 2).join(', ')}.`, code: paireCommune(t1, t2, ...pairesCcc[2]) ? 'cote_commun_oublie' : 'cote_oublie' },
        cac: paireCommune(t1, t2, ...pairesCac[1])
            ? { texte: `Un côté correspondant de même longueur et un angle de même mesure : ${cotesCac[0]}, ${angleCac}.`, code: 'cote_commun_oublie' }
            : { texte: `Deux côtés correspondants de même longueur : ${cotesCac.join(', ')}.`, code: 'angle_oublie' },
        aca: { texte: `Deux angles correspondants de même mesure : ${anglesAca.join(', ')}.`, code: acaCommun ? 'cote_commun_oublie' : 'cote_oublie' },
    }[c.cas];
    return {
        ccc: `${CAS.ccc} : ${cotesCcc.join(', ')}.`,
        cac: `${CAS.cac} : ${cotesCac.join(', ')}, ${angleCac}.`,
        aca: `Un côté ${acaCommun ? 'commun' : 'correspondant de même longueur'} et les deux angles qui le touchent de même mesure : ${coteAca}, ${anglesAca.join(', ')}.`,
        incomplet: incomplet.texte,
        codeIncomplet: incomplet.code,
    };
};

const rot = (pts) => { const d = randInt(0, 23) * 15; return tourner(pts, d); };

/** Les configurations codées : chacune ne justifie qu'UN cas (les autres données ne sont pas codées). */
const CONFIGURATIONS = [
    // cerf-volant coupé par sa diagonale : trois côtés (le côté commun n'est pas codé)
    () => {
        const [A, B, C, D] = pickLettresSains(POOL, 4);
        const w = 2 + Math.random() * 1.5; const h1 = 1.5 + Math.random(); const h2 = 3 + Math.random() * 1.5;
        const [pA, pB, pC, pD] = rot([[0, h1], [-w, 0], [0, -h2], [w, 0]]);
        return {
            cas: 'ccc', t1: `${A}${B}${C}`, t2: `${A}${D}${C}`,
            scene: {
                points: { [A]: pA, [B]: pB, [C]: pC, [D]: pD }, triangle: [A, B, C, D],
                segments: [[A, B], [B, C], [C, D], [D, A], [A, C]],
                ticks: [{ de: A, a: B, n: 1 }, { de: A, a: D, n: 1 }, { de: C, a: B, n: 2 }, { de: C, a: D, n: 2 }],
            },
        };
    },
    // parallélogramme coupé par sa diagonale : trois côtés (côtés opposés codés, la diagonale commune pas codée)
    () => {
        const [A, B, C, D] = pickLettresSains(POOL, 4);
        const a = 3.5 + Math.random() * 1.5; const h = 2 + Math.random() * 1.2; const dx = 0.8 + Math.random() * 1.2;
        const [pA, pB, pC, pD] = rot([[0, 0], [a, 0], [a + dx, h], [dx, h]]);
        return {
            cas: 'ccc', t1: `${A}${B}${C}`, t2: `${C}${D}${A}`,
            scene: {
                points: { [A]: pA, [B]: pB, [C]: pC, [D]: pD }, triangle: [A, B, C, D],
                segments: [[A, B], [B, C], [C, D], [D, A], [A, C]],
                ticks: [{ de: A, a: B, n: 1 }, { de: D, a: C, n: 1 }, { de: B, a: C, n: 2 }, { de: A, a: D, n: 2 }],
            },
        };
    },
    // papillon : O milieu des deux segments, angles opposés par le sommet (même mesure écrite)
    () => {
        const [O, A, B, C, D] = pickLettresSains(POOL, 5);
        const a = 2 + Math.random() * 1.2; const b = 1.4 + Math.random(); const t = (40 + randInt(0, 50)) * Math.PI / 180;
        const [pO, pA, pB, pC, pD] = rot([[0, 0], [-a, 0], [-b * Math.cos(t), -b * Math.sin(t)], [a, 0], [b * Math.cos(t), b * Math.sin(t)]]);
        const mesure = `${Math.round((t * 180) / Math.PI)}°`;
        return {
            cas: 'cac', t1: `${O}${A}${B}`, t2: `${O}${C}${D}`, sommetCompris: 0,
            scene: {
                points: { [O]: pO, [A]: pA, [B]: pB, [C]: pC, [D]: pD }, triangle: [A, B, C, D],
                segments: [[A, C], [B, D], [A, B], [C, D]],
                ticks: [{ de: O, a: A, n: 1 }, { de: O, a: C, n: 1 }, { de: O, a: B, n: 2 }, { de: O, a: D, n: 2 }],
                angles: [{ sommet: O, voisins: [A, B], texte: mesure }, { sommet: O, voisins: [C, D], texte: mesure }],
            },
        };
    },
    // triangles jumeaux (fiche du prof) : hauteur commune, angles droits, angles au sommet de même mesure
    () => {
        const [U, S, V, T] = pickLettresSains(POOL, 4);
        const h = 4 + Math.random(); const d = 1.6 + Math.random() * 1.2;
        const [pU, pS, pV, pT] = rot([[0, h], [-d, 0], [0, 0], [d, 0]]);
        const mesure = `${Math.round((Math.atan(d / h) * 180) / Math.PI)}°`;
        return {
            cas: 'aca', t1: `${S}${U}${V}`, t2: `${T}${U}${V}`, coteAngles: [1, 2],
            // le second angle droit ne vaut que si V est sur [ST] : le dessin le montre, l'énoncé le DIT (prof 25/09)
            donnee: `${tex(V)} est un point du segment ${tex(`[${S}${T}]`)}.`,
            scene: {
                points: { [U]: pU, [S]: pS, [V]: pV, [T]: pT }, triangle: [U, S, T],
                segments: [[U, S], [U, T], [S, T], [U, V]],
                carres: [{ sommet: V, voisins: [U, T] }],
                angles: [{ sommet: U, voisins: [S, V], texte: mesure }, { sommet: U, voisins: [V, T], texte: mesure }],
            },
        };
    },
    // deux triangles de part et d'autre d'un côté commun, les angles aux deux bouts codés
    () => {
        const [A, B, C, D] = pickLettresSains(POOL, 4);
        // deux mesures bien différentes (vu en PNG : 54° et 54° aux deux bouts brouillaient la lecture)
        const L = 4 + Math.random() * 1.5; const a = randInt(35, 60); const b = randomChoice([...Array(26)].map((_, i) => 35 + i).filter((x) => Math.abs(x - a) >= 8));
        const ra = (a * Math.PI) / 180; const rb = (b * Math.PI) / 180;
        const x = (L * Math.tan(rb)) / (Math.tan(ra) + Math.tan(rb)); const y = x * Math.tan(ra);
        const [pA, pB, pC, pD] = rot([[0, 0], [L, 0], [x, y], [x, -y]]);
        return {
            cas: 'aca', t1: `${A}${B}${C}`, t2: `${A}${B}${D}`, coteAngles: [0, 1],
            scene: {
                points: { [A]: pA, [B]: pB, [C]: pC, [D]: pD }, triangle: [A, B, C, D],
                segments: [[A, B], [A, C], [B, C], [A, D], [B, D]],
                angles: [
                    { sommet: A, voisins: [B, C], texte: `${a}°` }, { sommet: A, voisins: [B, D], texte: `${a}°` },
                    { sommet: B, voisins: [A, C], texte: `${b}°` }, { sommet: B, voisins: [A, D], texte: `${b}°` },
                ],
            },
        };
    },
    // triangle isocèle et sa bissectrice : deux côtés et l'angle compris (le côté commun n'est pas codé)
    () => {
        const [A, B, C, H] = pickLettresSains(POOL, 4);
        const h = 3.5 + Math.random() * 1.5; const d = 1.4 + Math.random() * 1.4;
        const [pA, pB, pC, pH] = rot([[0, h], [-d, 0], [d, 0], [0, 0]]);
        const mesure = `${Math.round((Math.atan(d / h) * 180) / Math.PI)}°`;
        return {
            cas: 'cac', t1: `${A}${B}${H}`, t2: `${A}${C}${H}`, sommetCompris: 0,
            scene: {
                points: { [A]: pA, [B]: pB, [C]: pC, [H]: pH }, triangle: [A, B, C],
                segments: [[A, B], [A, C], [B, C], [A, H]],
                ticks: [{ de: A, a: B, n: 1 }, { de: A, a: C, n: 1 }],
                angles: [{ sommet: A, voisins: [B, H], texte: mesure }, { sommet: A, voisins: [H, C], texte: mesure }],
            },
        };
    },
];

const genCas = () => {
    const c = randomChoice(CONFIGURATIONS)();
    const aria = `Les triangles ${c.t1} et ${c.t2}, avec leurs égalités codées.`;
    // La question demande le CAS qui prouve, pas un « pourquoi » (prof 25/09) : une
    // phrase vraie (« trois angles de même mesure », vraie par la somme des angles
    // dès que deux angles sont donnés) n'est pas une raison.
    const texte = `<p>${c.donnee ? `${c.donnee} ` : ''}Sur la figure, les égalités de longueurs et d'angles sont codées.</p>`
        + `<p><b>D'après ces données, quel cas d'égalité permet d'affirmer que les triangles ${tex(c.t1)} et ${tex(c.t2)} sont égaux ?</b></p>`;
    const rep = reponses(c);
    const choix = [rep.ccc, rep.cac, rep.aca, rep.incomplet];
    const payload = makeQcm(choix, rep[c.cas], texte, { ...opts(L_CAS), elements_additionnels: imgElement(svgTriangle({ ...c.scene, codages: true }, aria), 'large') });
    payload._meta.params = {
        cas: c.cas, t1: c.t1, t2: c.t2, alignement: !!c.donnee,
        distracteurs: [{ code: 'element_non_donne' }, { code: 'element_non_donne' }, { code: rep.codeIncomplet }],
    };
    return payload;
};

// ═══ Contrat distracteurs (référence de revue) ═════════════════════════════

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: ['figures exactes (décision prof 24/09) ; lettres saines en KaTeX ; tout se fait à l\'écran'],
    cas_qcm_unique: 'quatre réponses fixes : les trois cas et « trois angles de même mesure » (angles_seuls, jamais la clé) ; chaque configuration ne code que les données d\'UN cas ; le côté commun n\'est jamais codé (cote_commun_oublie)',
};

// ═══ Registre et point d'entrée ════════════════════════════════════════════

const GENERATORS = {
    sommet_placer_points_2d: genSommet,
    protocole_ordonner: genProtocole,
    cas_qcm_unique: genCas,
};

export const CATEGORIES = [
    { id: 'sommet', label: L_SOMMET, formatsList: [{ key: 'sommet_placer_points_2d', label: 'Placer (interactif)' }] },
    { id: 'protocole', label: L_PROTOCOLE, formatsList: [{ key: 'protocole_ordonner', label: 'Ordonner' }] },
    { id: 'cas', label: L_CAS, formatsList: [{ key: 'cas_qcm_unique', label: 'QCM' }] },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

